<template>
  <CCardBody class="history-table-body" :aria-busy="isLoading">
    <p v-if="isLoading" role="status" class="text-muted">{{ $t('asyncFeedback.loading') }}</p>
    <div v-else-if="loadError" role="alert" class="alert alert-danger">
      {{ $t('mgmt.loadFail') }}
      <button type="button" class="btn btn-outline-primary" @click="loadHistory">{{ $t('asyncFeedback.retry') }}</button>
    </div>
    <template v-if="hasLoaded && ((!isLoading && !loadError) || ticketsData.length)">
    <template v-if="ticketsData && ticketsData.length">
      <CDataTable ref="cDataTable" class="custom-pagination history-desktop-table" :items="filteredTicketsData" :fields="fields"
        :table-filter="tableFilterConfig"
        :table-filter-value.sync="desktopSearchQuery"
        :items-per-page-select="itemsPerPageSelectConfig"
        :items-per-page="tableItemsPerPage"
        :active-page.sync="historyPage"
        @pagination-change="tableItemsPerPage = $event"
        hover pagination>
        
        <template #ticketNumber="{ item }">
          <td :id="'ticket-row-' + (item.ticketNumber || item._id)" :data-ticket-id="item._id" :data-ticket-no="item.ticketNumber">
            <div class="history-ticket-number">{{ item.ticketNumber || '-' }}</div>
          </td>
        </template>

        <template #reported_datetime="{ item }">
          <td>
            <div class="history-date"><CIcon name="cil-calendar" size="sm" /> {{ formatDate(item.userPart.dateTimeInformed) }}</div>
          </td>
        </template>

        <template #issue_user="{ item }">
          <td>
            <div class="history-problem-details" :title="getProblemDetails(item)">
              {{ getProblemDetails(item) }}
            </div>
            <div v-if="item.userPart && item.userPart.onBehalfOf" class="small text-danger mt-1">
              ({{ $t('sendReport.confirmOnBehalf') || 'แจ้งแทน:' }} {{ item.userPart.onBehalfOf }})
            </div>
          </td>
        </template>

        <template #routing="{ item }">
          <td>
            <div class="history-routing-block">
              <div class="routing-item routing-item--section">
                <span class="routing-label">{{ $t('userHistory.section') || 'ส่วนงาน' }}</span>
                <span class="routing-value routing-value--section" :title="getCategoryName(item)">{{ getCategoryName(item) }}</span>
              </div>
              <div class="routing-item routing-item--dept">
                <span class="routing-label">{{ $t('userHistory.department') || 'ฝ่าย' }}</span>
                <span class="routing-value routing-value--dept" :class="{ 'text-muted': getDepartmentName(item) === '-' }" :title="getDepartmentName(item)">{{ getDepartmentName(item) }}</span>
              </div>
              <div class="routing-item routing-item--job">
                <span class="routing-label">{{ $t('userHistory.jobType') || 'ประเภทงาน' }}</span>
                <span class="routing-value routing-value--job" :title="getSubCategoryName(item)">{{ getSubCategoryName(item) }}</span>
              </div>
            </div>
          </td>
        </template>

        <template #reporter="{ item }">
          <td>
            <div class="history-issue">
              <span class="history-category">{{ item.username }}</span>
              <span class="history-subcategory">{{ item.userEmail }}</span>
              <span v-if="item._role === 'reporter' || historySource !== 'admin'" class="history-role-badge">{{ item._role === 'reporter' ? $t('userHistory.roleReporter') : $t('userHistory.roleAssigned') }}</span>
            </div>
          </td>
        </template>

        <template #rating="{ item }">
          <td class="text-center">
            <span v-if="item.status === 'cancelled'" class="history-cancelled-pill">
              {{ $t('dashboard.status.cancelled') || 'ยกเลิก' }}
            </span>
            <div v-else class="history-rating-pill">
              <span v-for="n in 5" :key="n" class="history-star"
                :style="{ color: n <= getRatingStars(item) ? '#FFD700' : '#E4E5E9' }">
                ★
              </span>
              <strong>{{ getRatingStars(item) }}/5</strong>
            </div>
          </td>
        </template>

        <template #actions="{ item }">
          <td class="text-center">
            <CButton size="sm" class="history-detail-btn" @click="onRowClick(item)"
              :title="$t('common.viewDetails')">
              <CIcon name="cil-magnifying-glass" size="sm" class="mr-1" /> {{ $t('common.viewDetails') }}
            </CButton>
          </td>
        </template>

        <template #no-results-view>
          <div class="text-center my-4 text-muted">
            <div class="mb-2"><CIcon name="cil-magnifying-glass" size="xl" style="opacity: 0.4;" /></div>
            <div>{{ $t('table.noResults') || 'ไม่พบข้อมูลที่ค้นหา' }}</div>
          </div>
        </template>

        <template #no-items-view>
          <div class="text-center my-4 text-muted">
            <div class="mb-2"><CIcon name="cil-magnifying-glass" size="xl" style="opacity: 0.4;" /></div>
            <div>{{ $t('table.noResults') || 'ไม่พบข้อมูลที่ค้นหา' }}</div>
          </div>
        </template>
      </CDataTable>

      <!-- Mobile & iPad Cards Section (< 992px) -->
      <div class="history-mobile-section">
        <!-- Card View Toolbar: Filter + Items per page -->
        <div class="mobile-table-toolbar">
          <div class="mobile-filter-group">
            <label class="mobile-filter-label">{{ $t('table.filterLabel') || 'ค้นหา:' }}</label>
            <div class="mobile-filter-input-wrap">
              <input
                v-model="mobileSearchQuery"
                type="text"
                class="mobile-search-input"
                :placeholder="$t('table.filterPlaceholder') || 'พิมพ์คำค้นหา...'"
              />
              <button
                v-if="mobileSearchQuery"
                type="button"
                class="mobile-search-clear"
                @click="mobileSearchQuery = ''"
              >
                ×
              </button>
            </div>
          </div>

          <div class="mobile-per-page-group">
            <label class="mobile-filter-label">{{ $t('table.itemsPerPageLabel') || 'แสดงต่อหน้า:' }}</label>
            <select v-model.number="mobileItemsPerPage" class="mobile-per-page-select">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>
        </div>

        <div v-if="mobileFilteredTickets.length" class="history-mobile-list">
          <article v-for="item in mobilePageTickets" :key="item._id" :id="'m-ticket-row-' + (item.ticketNumber || item._id)" :data-ticket-id="item._id" :data-ticket-no="item.ticketNumber" class="history-mobile-card">
            <div class="history-mobile-head">
              <strong>{{ item.ticketNumber || '-' }}</strong>
              <span>{{ formatDate(item.userPart && item.userPart.dateTimeInformed) }}</span>
            </div>

            <div class="history-mobile-problem">
              <span class="history-mobile-label">{{ $t('userHistory.problemDetails') || 'รายละเอียดปัญหา' }}:</span>
              <div class="history-mobile-problem-text">{{ getProblemDetails(item) }}</div>
              <div v-if="item.userPart && item.userPart.onBehalfOf" class="small text-danger mt-1">
                ({{ $t('sendReport.confirmOnBehalf') || 'แจ้งแทน:' }} {{ item.userPart.onBehalfOf }})
              </div>
            </div>

            <div v-if="!isTechnicianView" class="history-mobile-routing">
              <div class="history-mobile-routing-item">
                <span class="text-muted">{{ $t('userHistory.section') || 'ส่วนงาน' }}:</span>
                <strong>{{ getCategoryName(item) }}</strong>
              </div>
              <div class="history-mobile-routing-item">
                <span class="text-muted">{{ $t('userHistory.department') || 'ฝ่าย' }}:</span>
                <span>{{ getDepartmentName(item) }}</span>
              </div>
              <div class="history-mobile-routing-item">
                <span class="text-muted">{{ $t('userHistory.jobType') || 'ประเภทงาน' }}:</span>
                <span>{{ getSubCategoryName(item) }}</span>
              </div>
            </div>

            <div v-if="['technician', 'supervisor', 'admin', 'student_affairs'].includes(historySource)" class="history-mobile-reporter">
              <strong>{{ item.username }}</strong><span v-if="item._role === 'reporter' || historySource !== 'admin'">{{ item._role === 'reporter' ? $t('userHistory.roleReporter') : $t('userHistory.roleAssigned') }}</span>
            </div>
            <div class="history-mobile-foot">
              <span v-if="item.status === 'cancelled'" class="history-cancelled-pill small">
                {{ $t('dashboard.status.cancelled') || 'ยกเลิก' }}
              </span>
              <div v-else class="history-rating" :aria-label="getRatingStars(item) + ' / 5'">
                <span v-for="n in 5" :key="n" :class="{ filled: n <= getRatingStars(item) }">★</span>
              </div>
              <button type="button" @click.stop="onRowClick(item)"><CIcon name="cil-magnifying-glass" size="sm" /> {{ $t('common.viewDetails') }}</button>
            </div>
          </article>
        </div>

        <div v-else class="text-center my-4 py-4 text-muted border rounded bg-white">
          <div class="mb-2"><CIcon name="cil-magnifying-glass" size="xl" style="opacity: 0.4;" /></div>
          <div>{{ $t('table.noResults') || 'ไม่พบข้อมูลที่ค้นหา' }}</div>
        </div>

        <div v-if="mobileTotalPages > 1" class="mobile-list-pagination">
          <button type="button" :disabled="mobilePage === 1" @click="mobilePage--">‹</button>
          <span>{{ mobilePage }} / {{ mobileTotalPages }}</span>
          <button type="button" :disabled="mobilePage === mobileTotalPages" @click="mobilePage++">›</button>
        </div>
      </div>
    </template>

    <div v-else class="history-empty">
      <div class="history-empty-icon"><CIcon name="cil-history" size="xl" /></div>
      <p>{{ $t('userHistory.empty') }}</p>
    </div>

    </template>
  </CCardBody>
</template>

<style scoped>
.history-table-body { padding: 18px 14px; background: #fff; }
.history-ticket-number { display:inline-flex; align-items:center; padding:6px 9px; border-radius:8px; color:#1e40af; background:#f0f9ff; font-weight:800; font-size:.82rem; letter-spacing:.02em; white-space:nowrap; }
.history-date { display:flex; align-items:center; gap:5px; color:#526071; font-size:.8rem; white-space:nowrap; }
.history-date svg { color:#64748b; }
.history-issue { display:flex; min-width:0; flex-direction:column; gap:3px; }
.history-category { color:#2f2930; font-weight:800; font-size:.84rem; word-break:break-word; }
.history-subcategory { color:#6f6870; font-size:.78rem; word-break:break-all; overflow-wrap:anywhere; }
.history-role-badge { align-self:flex-start; padding:2px 7px; border-radius:6px; color:#1e40af; background:#f0f9ff; font-size:.68rem; font-weight:700; white-space:nowrap; }
.history-rating-pill { display:inline-flex; align-items:center; justify-content:center; gap:1px; padding:5px 8px; border:1px solid #f2e3b3; border-radius:999px; background:#fffaf0; white-space:nowrap; }
.history-star { margin:0 1px; font-size:15px; line-height:1; }
.history-rating-pill strong { margin-left:4px; color:#7c6541; font-size:.7rem; }
.history-cancelled-pill { display:inline-flex; align-items:center; justify-content:center; padding:4px 12px; border:1px solid #fecaca; border-radius:999px; background:#fef2f2; color:#dc2626; font-weight:700; font-size:.82rem; line-height:1.2; white-space:nowrap; }
.history-cancelled-pill.small { padding:3px 8px; font-size:.72rem; }

.history-problem-details {
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  color: #2f2930;
  font-size: 0.84rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.history-routing-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 5px 8px;
  background: #fbf9f9;
  border: 1px solid rgba(37, 99, 235, 0.08);
  border-radius: 8px;
  font-size: 0.78rem;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.routing-item {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.routing-label {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.routing-item--section .routing-label {
  background: #fbebeb;
  color: #1e40af;
}

.routing-item--dept .routing-label {
  background: #eef2f7;
  color: #2b4c7e;
}

.routing-item--job .routing-label {
  background: #f1f1f3;
  color: #4a4d52;
}

.routing-value {
  color: #2f2930;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.routing-value--section {
  font-weight: 700;
  color: #1e40af;
}

.routing-value--dept {
  color: #243a5e;
  font-weight: 600;
}

.routing-value--job {
  color: #4f5358;
}

.history-mobile-problem {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fdfbfb;
  border: 1px solid rgba(37, 99, 235,.06);
}

.history-mobile-label {
  font-size: 0.76rem;
  font-weight: 700;
  color: #1e40af;
}

.history-mobile-problem-text {
  margin-top: 2px;
  color: #2f2930;
  font-size: 0.86rem;
  line-height: 1.35;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}

.history-mobile-routing {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
}

.history-mobile-routing-item {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.history-desktop-table::v-deep .table-responsive { overflow-x: auto; border:1px solid rgba(37, 99, 235,.09); border-radius:14px; }
.history-desktop-table::v-deep table { width: 100%; margin-bottom:0; border-collapse:separate; border-spacing:0; }
.history-desktop-table::v-deep thead th { padding:12px 8px; border-top:0; border-bottom:1px solid rgba(37, 99, 235,.12); color:#1e40af; background:#f8fafc; font-size:.82rem; font-weight:800; vertical-align:middle; white-space: nowrap; }
.history-desktop-table::v-deep tbody td { padding:10px 8px; border-top:0; border-bottom:1px solid rgba(37, 99, 235,.07); background:#fff; vertical-align:middle; transition:background .18s ease; }
.history-desktop-table::v-deep tbody tr:last-child td { border-bottom:0; }
.history-desktop-table::v-deep tbody tr:hover td { background:#f8fafc; }
.history-desktop-table::v-deep .form-control { min-height:42px; border:1px solid rgba(37, 99, 235,.17); border-radius:10px; background:#fff; box-shadow:none; }
.history-desktop-table::v-deep .form-control:focus { border-color:#1d4ed8; box-shadow:0 0 0 3px rgba(37, 99, 235,.08); }
.history-desktop-table::v-deep .row:first-child { margin-bottom:16px; padding:13px 14px; border:1px solid rgba(37, 99, 235,.08); border-radius:12px; background:#f8fafc; }

.history-detail-btn {
  min-height: 34px;
  padding: 5px 10px;
  border: 1px solid #1e40af;
  border-radius: 8px;
  color: #fff;
  background: #1e40af;
  font-size: .76rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition:all .18s ease;
}
.history-detail-btn:hover { color:#fff; border-color:#1e3a8a; background:#1e3a8a; transform:translateY(-1px); box-shadow:0 7px 16px rgba(37, 99, 235,.2); }

.history-mobile-section { display: none; }
.history-empty { padding: 40px 16px; text-align: center; color: #776b6b; }
.history-empty-icon { display:inline-flex; align-items:center; justify-content:center; width:56px; height:56px; margin-bottom:12px; border-radius:18px; color:#1e40af; background:#f0f9ff; }

@media (max-width: 991.98px) {
  .history-table-body { padding: 12px; }
  .history-desktop-table { display: none !important; }
  .history-mobile-section { display: block !important; }
  .history-mobile-list { display: grid !important; gap: 14px; }
  .history-mobile-card {
    position: relative;
    overflow: hidden;
    padding: 16px;
    border: 1px solid rgba(37, 99, 235, 0.12);
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(37, 99, 235, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .history-mobile-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
  }
  .history-mobile-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
  .history-mobile-head strong { padding:5px 9px; border-radius:8px; color:#1e40af; background:#f0f9ff; font-size:1rem; border:1px solid rgba(37,99,235,0.12); }
  .history-mobile-head span { color:#64748b; font-size:.72rem; text-align:right; }
  .history-mobile-reporter { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:10px; padding:9px 10px; border-radius:8px; color:#475569; background:#f8fafc; border:1px solid #e2e8f0; font-size:.76rem; }
  .history-mobile-reporter strong { color:#1e293b; }
  .history-mobile-reporter span { color:#1e40af; font-weight:700; }
  .history-mobile-foot { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:14px; padding-top:11px; border-top:1px solid rgba(37, 99, 235,.08); }
  .history-rating { white-space:nowrap; }
  .history-rating span { color:#cbd5e1; font-size:1rem; }
  .history-rating span.filled { color:#ffc107; }
  .history-mobile-foot button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 14px;
    border: 0;
    border-radius: 9px;
    color: #fff;
    background: #1e40af;
    font-size: .8rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .history-mobile-foot button:hover {
    background: #172554;
  }
}

@media (min-width: 576px) and (max-width: 991.98px) {
  .history-mobile-list {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 16px;
  }
}

/* Mobile Table Toolbar (< 992px) */
.mobile-table-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid rgba(37, 99, 235, 0.09);
  border-radius: 12px;
}

.mobile-filter-group,
.mobile-per-page-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-group {
  flex: 1 1 240px;
}

.mobile-per-page-group {
  flex: 0 0 auto;
}

.mobile-filter-label {
  margin-bottom: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #475569;
  white-space: nowrap;
}

.mobile-filter-input-wrap {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.mobile-search-input {
  width: 100%;
  height: 38px;
  padding: 6px 32px 6px 12px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.88rem;
  color: #1e293b;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mobile-search-input:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.mobile-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.mobile-search-clear:hover {
  color: #dc2626;
}

.mobile-per-page-select {
  height: 38px;
  padding: 6px 12px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 8px;
  background-color: #ffffff;
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mobile-per-page-select:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.mobile-list-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin: 24px auto 12px;
}

.mobile-list-pagination button {
  min-width: 38px;
  height: 38px;
  color: #1e40af;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-list-pagination button:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: #1e40af;
}

.mobile-list-pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-list-pagination span {
  font-size: 0.88rem;
  font-weight: 700;
  color: #475569;
}
</style>

<script>
import { assertListResponse, queueLatestListLoad, disposeListLoad } from "@/utils/asyncFeedback";
import { mapGetters } from "vuex";
import Service from '@/service/api';
import {
  getTicketProblemDetails,
  getTicketSectionName,
  getTicketJobTypeName,
  getTicketDepartmentName,
} from '@/utils/ticketViewHelper';
import { matchTicketBilingual } from "@/utils/buildingBilingual.js";

export default {
  name: "UHistoryTable",
  props: {
    historySource: {
      type: String,
      default: 'user',
      validator: value => ['user', 'technician', 'supervisor', 'admin', 'student_affairs'].includes(value),
    },
  },
  data() {
    return {
      ticketsData: [],
      isLoading: false,
      loadError: false,
      hasLoaded: false,
      departmentsMap: {},
      historyPage: 1,
      tableItemsPerPage: 10,
      desktopSearchQuery: '',
      mobilePage: 1,
      mobileSearchQuery: '',
      mobileItemsPerPage: 10,
    };
  },
  computed: {
    itemsPerPageSelectConfig() {
      return {
        label: this.$t('table.itemsPerPageLabel'),
        values: [10, 20, 50]
      };
    },
    tableFilterConfig() {
      return {
        label: this.$t('table.filterLabel'),
        placeholder: this.$t('table.filterPlaceholder'),
        external: true,
      };
    },
    currentLang() {
      return this.$store?.state?.locale?.lang || "th";
    },
    ...mapGetters("auth", ["profile"]),
    filteredTicketsData() {
      const q = (this.desktopSearchQuery || '').trim();
      if (!q) return this.ticketsData || [];
      return (this.ticketsData || []).filter(ticket => this.matchTicket(ticket, q));
    },
    mobileFilteredTickets() {
      const q = (this.mobileSearchQuery || '').trim();
      if (!q) return this.ticketsData || [];
      return (this.ticketsData || []).filter(ticket => this.matchTicket(ticket, q));
    },
    mobilePageTickets() {
      const perPage = this.mobileItemsPerPage || 10;
      const start = (this.mobilePage - 1) * perPage;
      return this.mobileFilteredTickets.slice(start, start + perPage);
    },
    mobileTotalPages() {
      const perPage = this.mobileItemsPerPage || 10;
      return Math.max(1, Math.ceil(this.mobileFilteredTickets.length / perPage));
    },
    isTechnicianView() {
      return this.historySource === 'technician' || (this.profile && this.profile.role === 'technician');
    },
    fields() {
      void this.$store?.state?.locale?.lang;
      if (this.isTechnicianView) {
        return [
          { key: 'ticketNumber', label: this.$t('userHistory.ticketNo'), _style: 'width:12%; min-width: 110px;' },
          { key: 'reported_datetime', label: this.$t('userHistory.dateTime'), _style: 'width:15%; min-width: 120px;' },
          { key: 'reporter', label: this.$t('userHistory.reporterAndRole'), _style: 'width:22%; min-width: 150px;' },
          { key: 'issue_user', label: this.$t('userHistory.problemDetails') || this.$t('userHistory.details'), _style: 'width:33%; min-width: 200px;' },
          { key: 'rating', label: this.$t('userHistory.satisfaction'), _style: 'width:10%; text-align:center; min-width: 90px;', class: 'text-center' },
          { key: 'actions', label: '', _style: 'width:8%; text-align:center; min-width: 70px;', class: 'text-center', sorter: false, filter: false },
        ];
      }
      if (['supervisor', 'admin', 'student_affairs'].includes(this.historySource)) {
        return [
          { key: 'ticketNumber', label: this.$t('userHistory.ticketNo'), _style: 'width:9%; min-width: 85px;' },
          { key: 'reported_datetime', label: this.$t('userHistory.dateTime'), _style: 'width:12%; min-width: 100px;' },
          { key: 'reporter', label: this.$t('userHistory.reporterAndRole'), _style: 'width:16%; min-width: 120px;' },
          { key: 'issue_user', label: this.$t('userHistory.problemDetails') || this.$t('userHistory.details'), _style: 'width:17%; min-width: 110px;' },
          { key: 'routing', label: this.$t('userHistory.routing') || 'หน่วยงานที่รับแจ้ง', _style: 'width:24%; min-width: 165px;' },
          { key: 'rating', label: this.$t('userHistory.satisfaction'), _style: 'width:12%; text-align:center; min-width: 85px;', class: 'text-center' },
          { key: 'actions', label: '', _style: 'width:10%; min-width: 80px;', sorter: false, filter: false },
        ];
      }
      return [
        { key: "ticketNumber", label: this.$t('userHistory.ticketNo'), _style: "width:12%; min-width: 110px;" },
        { key: "reported_datetime", label: this.$t('userHistory.dateTime'), _style: "width:14%; min-width: 120px;" },
        { key: "issue_user", label: this.$t('userHistory.problemDetails') || this.$t('userHistory.details'), _style: "width:28%; min-width: 180px;" },
        { key: "routing", label: this.$t('userHistory.routing') || 'หน่วยงานที่รับแจ้ง', _style: "width:32%; min-width: 230px;" },
        {
          key: "rating",
          label: this.$t('userHistory.satisfaction'),
          _style: "width:8%; text-align:center; min-width: 80px;",
          class: "text-center"
        },
        { key: "actions", label: "", _style: "width:6%; min-width: 50px;", sorter: false, filter: false },
      ];
    },
  },
  watch: {
    profile(val) {
      if (val) this.loadHistory();
    },
    desktopSearchQuery() {
      this.historyPage = 1;
      if (this.$refs.cDataTable && this.$refs.cDataTable.page !== 1) {
        this.$refs.cDataTable.page = 1;
      }
    },
    mobileSearchQuery() {
      this.mobilePage = 1;
    },
    mobileItemsPerPage() {
      this.mobilePage = 1;
    },
    mobileTotalPages(val) {
      if (this.mobilePage > val) this.mobilePage = val;
    },
    historyPage(val) {
      if (this.$refs.cDataTable && this.$refs.cDataTable.page !== val) {
        this.$refs.cDataTable.page = val;
      }
    },
    '$route.query': {
      handler(query) {
        if (query && (query.ticketId || query.ticketNo)) {
          this.$nextTick(() => {
            this.jumpToTicket(query.ticketId, query.ticketNo);
          });
        }
      }
    },
  },
  mounted() {
    this.loadDepartments();
    this.loadHistory();
  },
  beforeDestroy() {
    disposeListLoad(this);
  },
  methods: {
    async loadDepartments() {
      try {
        const res = await Service.department('get', null, { urlAppend: '/all' });
        if (res.data?.success && Array.isArray(res.data.data)) {
          const map = {};
          res.data.data.forEach(d => {
            if (d && d._id) map[String(d._id)] = d;
          });
          this.departmentsMap = map;
        }
      } catch (_) {
        // Neutral fallback if departments endpoint is unreachable
      }
    },
    loadHistory() {
      return queueLatestListLoad(this, async isCurrent => {
        this.loadError = false;
        try {
          const userStr = localStorage.getItem('user');
          const userObj = userStr ? JSON.parse(userStr) : {};
          const userId = this.profile?._id || this.profile?.id || userObj._id || userObj.id;
          if (!userId) {
            throw new Error("User ID not found");
          }

          if (this.historySource === 'admin') {
            const response = await Service.ticket('get', null, { urlAppend: '/admin/all' }).then(assertListResponse);
            if (!isCurrent()) return;
            const tickets = response.data.success && Array.isArray(response.data.data)
              ? response.data.data
                .filter(ticket => ['done', 'welldone', 'cancelled', 'cancel'].includes(ticket.status))
                .map(ticket => this.withTechnicianMeta(ticket, String(ticket.userPart?.user?._id || ticket.userPart?.user || '') === String(userId) ? 'reporter' : 'admin'))
              : [];
            this.ticketsData = tickets.sort((a, b) => new Date(b.userPart?.dateTimeInformed || b.createdAt) - new Date(a.userPart?.dateTimeInformed || a.createdAt));
          } else if (this.historySource === 'technician') {
            const [assignedRes, reportedRes] = await Promise.all([
              Service.ticket('get', null, { urlAppend: `/technician/${userId}/assigned` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse),
            ]);
            if (!isCurrent()) return;
            let tickets = [];
            if (assignedRes.data.success && Array.isArray(assignedRes.data.data)) {
              tickets = assignedRes.data.data.filter(ticket => ['done', 'welldone'].includes(ticket.status)).map(ticket => this.withTechnicianMeta(ticket, 'assignee'));
            }
            if (reportedRes.data.success && Array.isArray(reportedRes.data.data)) {
              const existingIds = new Set(tickets.map(ticket => String(ticket._id)));
              tickets.push(...reportedRes.data.data.filter(ticket => !existingIds.has(String(ticket._id))).map(ticket => this.withTechnicianMeta(ticket, 'reporter')));
            }
            this.ticketsData = tickets.sort((a, b) => new Date(b.userPart?.dateTimeInformed || b.createdAt) - new Date(a.userPart?.dateTimeInformed || a.createdAt));
          } else if (this.historySource === 'supervisor') {
            const sectionValue = this.profile.section || this.profile.sectionId || this.profile.category;
            const sectionId = sectionValue && (sectionValue._id || sectionValue);
            if (!sectionId) throw new Error('Supervisor section not found');
            const jobTypeParam = this.profile.jobType ? `?jobType=${encodeURIComponent(this.profile.jobType)}` : '';
            const [departmentRes, reportedRes] = await Promise.all([
              Service.ticket('get', null, { urlAppend: `/supervisor/${sectionId}/all${jobTypeParam}` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse),
            ]);
            if (!isCurrent()) return;
            let tickets = departmentRes.data.success && Array.isArray(departmentRes.data.data)
              ? departmentRes.data.data.filter(ticket => ['done', 'welldone'].includes(ticket.status)).map(ticket => this.withTechnicianMeta(ticket, 'dept'))
              : [];
            const existingIds = new Set(tickets.map(ticket => String(ticket._id)));
            if (reportedRes.data.success && Array.isArray(reportedRes.data.data)) {
              tickets.push(...reportedRes.data.data.filter(ticket => !existingIds.has(String(ticket._id))).map(ticket => this.withTechnicianMeta(ticket, 'reporter')));
            }
            this.ticketsData = tickets.sort((a, b) => new Date(b.userPart?.dateTimeInformed || b.createdAt) - new Date(a.userPart?.dateTimeInformed || a.createdAt));
          } else {
            const response = await Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse);
            if (!isCurrent()) return;
            if (response.data.success) this.ticketsData = response.data.data;
          }

          if (this.$route.query && (this.$route.query.ticketId || this.$route.query.ticketNo)) {
            this.$nextTick(() => {
              if (!isCurrent()) return;
              this.jumpToTicket(this.$route.query.ticketId, this.$route.query.ticketNo);
            });
          }
          this.hasLoaded = true;
        } catch (error) {
          if (!isCurrent()) return;
          console.error("Failed to load history:", error);
          this.loadError = true;
        }
      });
    },
    withTechnicianMeta(ticket, role) {
      const user = ticket.userPart?.user || {};
      return {
        ...ticket,
        _role: role,
        username: `${user.name || ''} ${user.surname || ''}`.trim() || 'ไม่ระบุตัวตน',
        userEmail: user.email || '-',
      };
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const isEn = this.currentLang === "en";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      if (isEn) {
        const enMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = enMonths[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year} ${hours}:${minutes}`;
      } else {
        const thaiMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
        const month = thaiMonths[date.getMonth()];
        const year = String(date.getFullYear() + 543).slice(-2);
        return `${day} ${month} ${year} ${hours}:${minutes} น.`;
      }
    },
    getProblemDetails(item) {
      return getTicketProblemDetails(item);
    },
    getCategoryName(item) {
      return getTicketSectionName(item);
    },
    getSubCategoryName(item) {
      return getTicketJobTypeName(item);
    },
    getDepartmentName(item) {
      return getTicketDepartmentName(item, this.departmentsMap);
    },
    getRatingStars(item) {
      return item.rating?.stars || 0;
    },
    matchTicket(ticket, rawQuery) {
      if (!ticket || !rawQuery || !rawQuery.trim()) return true;
      const q = rawQuery.trim().toLowerCase();

      // 1. Bilingual matcher (building names in TH & EN, building aliases e.g. C1, E4, room numbers, outdoor locations, problem details, reporter name/dept/affiliation, ticket number, bilingual statuses)
      if (matchTicketBilingual(ticket, rawQuery)) return true;

      // 2. Extra ticket attributes displayed in the table
      const ticketNum = String(ticket.ticketNumber || ticket.ticketNo || ticket._id || '').toLowerCase();
      if (ticketNum.includes(q)) return true;

      const details = String(this.getProblemDetails(ticket) || '').toLowerCase();
      if (details.includes(q)) return true;

      const cat = String(this.getCategoryName(ticket) || '').toLowerCase();
      if (cat.includes(q)) return true;

      const dept = String(this.getDepartmentName(ticket) || '').toLowerCase();
      if (dept.includes(q)) return true;

      const subcat = String(this.getSubCategoryName(ticket) || '').toLowerCase();
      if (subcat.includes(q)) return true;

      const username = String(ticket.username || '').toLowerCase();
      if (username.includes(q)) return true;

      const userEmail = String(ticket.userEmail || '').toLowerCase();
      if (userEmail.includes(q)) return true;

      const onBehalf = String(ticket.userPart?.onBehalfOf || '').toLowerCase();
      if (onBehalf.includes(q)) return true;

      const roleText = String(
        ticket._role === 'reporter'
          ? this.$t('userHistory.roleReporter')
          : this.$t('userHistory.roleAssigned')
      ).toLowerCase();
      if (roleText.includes(q)) return true;

      return false;
    },
    onRowClick(item) {
      this.$emit("show-detail", item);
    },
    jumpToTicket(ticketId, ticketNo) {
      if (!ticketId && !ticketNo) return;
      const targetId = String(ticketId || '').toLowerCase();
      const targetNo = String(ticketNo || '').toLowerCase();

      if (this.$refs.cDataTable) {
        this.$refs.cDataTable.tableFilterState = '';
      }
      this.desktopSearchQuery = '';
      this.mobileSearchQuery = '';

      const items = (this.$refs.cDataTable && this.$refs.cDataTable.sortedItems) || this.ticketsData;
      const idx = items.findIndex(t => {
        const id = String(t._id || t.id || '').toLowerCase();
        const no = String(t.ticketNumber || (id ? id.substring(0, 8).toUpperCase() : '')).toLowerCase();
        return (targetId && id === targetId) || (targetNo && (no === targetNo || id.substring(0, 8).toUpperCase() === targetNo.toUpperCase()));
      });

      if (idx !== -1) {
        const perPage = (this.$refs.cDataTable && this.$refs.cDataTable.perPageItems) || 10;
        const targetPage = Math.floor(idx / perPage) + 1;
        this.historyPage = targetPage;
        if (this.$refs.cDataTable) {
          this.$refs.cDataTable.page = targetPage;
        }
        this.mobilePage = Math.floor(idx / this.mobileItemsPerPage) + 1;

        this.$nextTick(() => {
          if (this.$refs.cDataTable && this.$refs.cDataTable.page !== targetPage) {
            this.$refs.cDataTable.page = targetPage;
          }
          const links = this.$el ? this.$el.querySelectorAll('.pagination .page-item .page-link') : [];
          links.forEach(link => {
            if (link.textContent && link.textContent.trim() === String(targetPage)) {
              link.click();
            }
          });
        });

        this.retryScrollToAndHighlight([
          'm-ticket-row-' + ticketNo,
          'm-ticket-row-' + ticketId,
          'ticket-row-' + ticketNo,
          'ticket-row-' + ticketId,
          ticketNo,
          ticketId
        ]);
      }
    },
    scrollElementToCenter(target) {
      if (!target) return;
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ];
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      );
      const bottomNavHeight = 90;
      const headerHeight = 70;

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const currentScroll = container.scrollTop;
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll;
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight);
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2);

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      } else {
        const targetRect = target.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight);
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2);

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      }
    },
    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false;
        }
        return true;
      };

      let foundTarget = null;
      for (const id of ids) {
        if (!id) continue;
        const rawId = String(id).replace(/^ticket-row-|^m-ticket-row-/, '');
        const candidates = [
          document.getElementById('m-ticket-row-' + rawId),
          document.getElementById('ticket-row-' + rawId),
          document.getElementById(id),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean);

        for (const el of candidates) {
          const target = el.closest('.history-mobile-card') ||
                         el.closest('.active-mobile-card') ||
                         el.closest('.mobile-ticket-card') ||
                         el.closest('tr') ||
                         el;
          if (isVisible(target)) {
            target.classList.remove('match-highlight-active');
            void target.offsetWidth;
            target.classList.add('match-highlight-active');
            setTimeout(() => {
              target.classList.remove('match-highlight-active');
            }, 3600);
            if (!foundTarget) {
              foundTarget = target;
            }
          }
        }
      }

      if (foundTarget) {
        this.scrollElementToCenter(foundTarget);
        return true;
      }
      return false;
    },
    retryScrollToAndHighlight(ids, attempts = 10) {
      this.$nextTick(() => {
        const found = this.scrollToAndHighlight(ids);
        if (!found && attempts > 0) {
          setTimeout(() => {
            this.retryScrollToAndHighlight(ids, attempts - 1);
          }, 100);
        }
      });
    },
  },
};
</script>
